import type { ReactElement } from 'react';
import type { VizAccent } from '@/design/VizAccents';
import { formatCompact, formatCount, formatPercent, formatUsd, peakShareFill } from '@/shared/analytics/MetricFormatting';
import RankedList, { type RankedListColumn, type RankedListPrimary } from '@/design/RankedList/RankedList';

/** One cost row: a label, its token spend + allocated $ cost + share, a secondary stat, optional nested rows, and an optional drill target. */
export interface CostListRow {
    /** Stable key. */
    id: string;
    /** Drill target; omit for an expand-only row (e.g. a department that reveals its people). */
    to?: string;
    label: string;
    /** Dimmed subline beneath the label (e.g. a department's people count). */
    subline?: string;
    tokens: number;
    /** Allocated real spend ($); 0 when no cost data covers the row. */
    costUsd: number;
    share: number;
    secondaryValue: number;
    secondaryLabel: string;
    /** Nested rows revealed on expand (e.g. the people in a department). */
    children?: CostListRow[];
}

interface TokenUsageCostRankedListProps {
    rows: CostListRow[];
    accent: VizAccent;
    /** Header for the leading label column, e.g. "Department / person". */
    primaryHeader: string;
    /** What the top-level rows represent, used in the caption (e.g. "departments"). */
    noun: string;
}

/** A high page size so the list never paginates — it scrolls inside RankedList's own container instead. */
const NO_PAGINATION = 1000;

function capitalize(text: string): string {
    return text.length === 0 ? text : `${text.charAt(0).toUpperCase()}${text.slice(1)}`;
}

/**
 * The Cost drill ranked list — a real sortable table (the shared {@link RankedList})
 * so it reads like the Team Spend list: a labelled leading column with a share
 * bar, then Total tokens / Cost / Sessions, each with a sortable header. Rows with
 * `children` expand in place (e.g. a department revealing its people); rows with a
 * `to` drill into their evidence. Bar widths stay scaled to the overall token peak,
 * and the list scrolls rather than paginating.
 */
export default function TokenUsageCostRankedList({ rows, accent, primaryHeader, noun }: TokenUsageCostRankedListProps): ReactElement {
    const max = rows.reduce((peak, row) => Math.max(peak, row.tokens), 0);
    const secondaryHeader = capitalize(rows[0]?.secondaryLabel ?? '');

    const primary: RankedListPrimary<CostListRow> = {
        key: 'label',
        header: primaryHeader,
        label: (row) => row.label,
        subline: (row) => row.subline,
        fill: (row) => peakShareFill(row.tokens, max),
        share: (row) => formatPercent(row.share),
        sort: { get: (row) => row.label, initialDir: 'asc' },
    };

    const columns: RankedListColumn<CostListRow>[] = [
        { key: 'tokens', header: 'Total tokens', width: 84, render: (row) => formatCompact(row.tokens), sort: { get: (row) => row.tokens, initialDir: 'desc' } },
        { key: 'cost', header: 'Cost', width: 72, render: (row) => formatUsd(row.costUsd), sort: { get: (row) => row.costUsd, initialDir: 'desc' }, visibleFrom: 'sm', size: 'xs' },
        { key: 'secondary', header: secondaryHeader, width: 64, render: (row) => formatCount(row.secondaryValue), sort: { get: (row) => row.secondaryValue, initialDir: 'desc' }, visibleFrom: 'sm', size: 'xs' },
    ];

    return (
        <RankedList
            rows={rows}
            primary={primary}
            columns={columns}
            rowKey={(row) => row.id}
            accent={accent}
            initialSort={{ key: 'tokens', dir: 'desc' }}
            hrefFor={(row) => row.to}
            childrenFor={(row) => row.children}
            unitPlural={noun}
            pageSize={NO_PAGINATION}
        />
    );
}
